// ==========================================================================
// Google Maps Loader - Singleton loader with async best practices
// Uses new APIs: AdvancedMarkerElement, AutocompleteSuggestion, Place
// ==========================================================================

const GOOGLE_MAPS_API_KEY = 'AIzaSyDfereJAJWqNQf78j5RhUKaUGTeQ3QZ1bc';

let loadPromise: Promise<void> | null = null;

/**
 * Load Google Maps API using recommended async pattern
 * Includes: places (new Place API), marker (AdvancedMarkerElement)
 * @see https://developers.google.com/maps/documentation/javascript/load-maps-js-api
 */
export function loadGoogleMaps(): Promise<void> {
  // Already loaded
  if (window.google?.maps?.Map && window.google?.maps?.marker?.AdvancedMarkerElement) {
    return Promise.resolve();
  }

  // Currently loading - return existing promise
  if (loadPromise) {
    return loadPromise;
  }

  // Start loading
  loadPromise = new Promise((resolve, reject) => {
    // Check if script already exists in DOM
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    
    if (existingScript) {
      // Script tag exists, wait for google.maps to be available
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.Map && window.google?.maps?.marker?.AdvancedMarkerElement) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 50);
      
      // Timeout after 10s
      setTimeout(() => {
        clearInterval(checkLoaded);
        if (window.google?.maps?.Map) {
          resolve();
        } else {
          reject(new Error('Google Maps failed to load'));
        }
      }, 10000);
      return;
    }

    // Create callback for async loading
    const callbackName = '__googleMapsCallback__';
    
    (window as unknown as Record<string, () => void>)[callbackName] = () => {
      delete (window as unknown as Record<string, () => void>)[callbackName];
      resolve();
    };

    // Create script with async loading pattern
    // Libraries: places (new Place API), marker (AdvancedMarkerElement)
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,marker&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    
    script.onerror = () => {
      delete (window as unknown as Record<string, () => void>)[callbackName];
      loadPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };
    
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Check if Google Maps is fully loaded (including marker library)
 */
export function isGoogleMapsLoaded(): boolean {
  return !!(window.google?.maps?.Map && window.google?.maps?.marker?.AdvancedMarkerElement);
}
